import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchSearchOptions, fetchSearchResults } from '../services/searchApi';

const copy = {
  ja: {
    title: 'お酒を検索', category: 'カテゴリ', classification: '酒種', all: 'すべて', latest: '新着順',
    query: '検索文', placeholder: '名前、または好みや気分を入力', keywordSearch: '名前で検索',
    extendedSearch: '拡張検索', reset: 'リセット', searching: '検索中…', count: count => `${count}件`, noData: '該当するお酒はありません。',
    extendedNote: '拡張検索は、感想文の雰囲気が近いお酒を関連度順に表示します。意外な候補が含まれることがあります。',
    error: '検索結果を取得できませんでした。時間をおいて再度お試しください。', previous: '前へ', next: '次へ',
    detected: name => `検索文から「${name}」を選びました。`, tooLong: '検索文は500文字以内で入力してください。'
  },
  en: {
    title: 'Search drinks', category: 'Category', classification: 'Type', all: 'All', latest: 'Latest',
    query: 'Search query', placeholder: 'Enter a name, taste, or mood', keywordSearch: 'Search by name',
    extendedSearch: 'Smart search', reset: 'Reset', searching: 'Searching…', count: count => `${count} results`, noData: 'No drinks found.',
    extendedNote: 'Smart search ranks results by similarity to our tasting notes and may include unexpected suggestions.',
    error: 'Could not load search results. Please try again.', previous: 'Previous', next: 'Next',
    detected: name => `Category selected from your query: ${name}`, tooLong: 'Please enter no more than 500 characters.'
  }
};

function readLocation() {
  const params = new URLSearchParams(window.location.search);
  return {
    query: params.get('q') || '',
    categoryId: params.get('category') || '',
    classificationId: params.get('classification') || '0',
    mode: params.get('mode') === 'extended' ? 'extended' : 'browse',
    page: Math.max(Number(params.get('page')) || 1, 1)
  };
}

export default function SearchPage({ config }) {
  const locale = config.locale === 'en' ? 'en' : 'ja';
  const t = copy[locale];
  const initial = useRef(readLocation()).current;
  const [query, setQuery] = useState(initial.query);
  const [categoryId, setCategoryId] = useState(initial.categoryId);
  const [classificationId, setClassificationId] = useState(initial.classificationId);
  const [categories, setCategories] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [result, setResult] = useState({ results: [], pagination: null, category: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const requestRef = useRef(null);

  const loadOptions = useCallback(async (selectedCategoryId) => {
    const data = await fetchSearchOptions(config.optionsUrl, selectedCategoryId);
    setCategories(data.categories);
    setClassifications(data.classifications);
    return data;
  }, [config.optionsUrl]);

  const runSearch = useCallback(async (state, historyMode = 'push') => {
    if (state.query.length > 500) {
      setError(t.tooLong);
      return;
    }

    const visibleParams = new URLSearchParams();
    if (state.query) visibleParams.set('q', state.query);
    if (state.categoryId) visibleParams.set('category', state.categoryId);
    if (state.classificationId && state.classificationId !== '0') visibleParams.set('classification', state.classificationId);
    if (state.mode === 'extended') visibleParams.set('mode', 'extended');
    if (state.page > 1) visibleParams.set('page', String(state.page));
    const visibleUrl = `${window.location.pathname}${visibleParams.toString() ? `?${visibleParams}` : ''}`;
    if (historyMode === 'push') window.history.pushState(null, '', visibleUrl);
    if (historyMode === 'replace') window.history.replaceState(null, '', visibleUrl);

    if (requestRef.current) requestRef.current.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    setError('');

    try {
      const data = await fetchSearchResults(config, state, locale, controller.signal);
      setResult(data);
    } catch (fetchError) {
      if (fetchError.name !== 'AbortError') setError(t.error);
    } finally {
      if (requestRef.current === controller) setLoading(false);
    }
  }, [config, locale, t.error, t.tooLong]);

  useEffect(() => {
    loadOptions(initial.categoryId)
      .then(options => {
        const validClassification = initial.classificationId === '0'
          || options.classifications.some(item => String(item.id) === initial.classificationId);
        const state = {
          ...initial,
          classificationId: validClassification ? initial.classificationId : '0'
        };
        setClassificationId(state.classificationId);
        return runSearch(state, 'replace');
      })
      .catch(() => { setError(t.error); setLoading(false); });

    const onPopState = () => {
      const state = readLocation();
      setQuery(state.query);
      setCategoryId(state.categoryId);
      loadOptions(state.categoryId).then(options => {
        const validClassification = state.classificationId === '0'
          || options.classifications.some(item => String(item.id) === state.classificationId);
        const nextClassificationId = validClassification
          ? state.classificationId
          : '0';
        setClassificationId(nextClassificationId);
        return runSearch({ ...state, classificationId: nextClassificationId }, 'none');
      }).catch(() => setError(t.error));
    };
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
      if (requestRef.current) requestRef.current.abort();
    };
  }, []); // URLを初期状態として一度だけ読み込む。

  const changeCategory = async (event) => {
    const nextCategoryId = event.target.value;
    setCategoryId(nextCategoryId);
    setClassificationId('0');
    try {
      await loadOptions(nextCategoryId);
      await runSearch({ query: '', categoryId: nextCategoryId, classificationId: '0', mode: 'browse', page: 1 });
      setQuery('');
    } catch (_) { setError(t.error); }
  };

  const changeClassification = (event) => {
    const next = event.target.value;
    setClassificationId(next);
    setQuery('');
    runSearch({ query: '', categoryId, classificationId: next, mode: 'browse', page: 1 });
  };

  const submit = (event, mode) => {
    event.preventDefault();
    runSearch({ query: query.trim(), categoryId, classificationId, mode, page: 1 });
  };

  const resetSearch = async () => {
    setQuery('');
    setCategoryId('');
    setClassificationId('0');
    try {
      await loadOptions('');
      await runSearch({ query: '', categoryId: '', classificationId: '0', mode: 'browse', page: 1 });
    } catch (_) { setError(t.error); }
  };

  const movePage = page => runSearch({ query, categoryId, classificationId, mode: readLocation().mode, page });
  const detailUrl = url => {
    const detail = new URL(url, window.location.origin);
    detail.searchParams.set('return_to', `${window.location.pathname}${window.location.search}`);
    return `${detail.pathname}${detail.search}`;
  };
  const pagination = result.pagination;

  return (
    <main className="react-search-page">
      <h1>{t.title}</h1>
      <form className="search-panel" onSubmit={event => submit(event, 'browse')}>
        <div className="search-filters">
          <label>{t.category}
            <select value={categoryId} onChange={changeCategory}>
              <option value="">{t.all}</option>
              {categories.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </label>
          <label>{t.classification}
            <select value={classificationId} onChange={changeClassification}>
              {!classifications.some(item => String(item.id) === '0') && <option value="0">{t.latest}</option>}
              {classifications.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </label>
        </div>
        <label className="search-query">{t.query}
          <textarea value={query} maxLength="500" rows="3" placeholder={t.placeholder} onChange={event => setQuery(event.target.value)} />
          <span className="search-counter">{query.length} / 500</span>
        </label>
        <div className="search-actions">
          <button type="submit" disabled={!query.trim() || loading}>{t.keywordSearch}</button>
          <button type="button" className="extended" disabled={!query.trim() || loading} onClick={event => submit(event, 'extended')}>{t.extendedSearch}</button>
          <button type="button" className="reset" disabled={loading} onClick={resetSearch}>{t.reset}</button>
        </div>
        <p className="extended-search-note">{t.extendedNote}</p>
      </form>

      <section className="search-results" aria-live="polite" aria-busy={loading}>
        {loading && <p className="search-status">{t.searching}</p>}
        {error && <p className="search-error" role="alert">{error}</p>}
        {!loading && !error && result.category && <p className="search-hint">{t.detected(result.category.name)}</p>}
        {!loading && !error && pagination && <p className="search-count">{t.count(pagination.total_count)}</p>}
        {!loading && !error && result.results.length === 0 && <p className="search-status">{t.noData}</p>}
        {!error && result.results.map(item => (
          <article className="search-result" key={item.drink_id}>
            <a href={detailUrl(item.detail_url)} data-turbolinks="false">
              {item.image_url && <img src={item.image_url} alt="" />}
              <span><strong>{item.name}</strong>{item.classification && <small>{item.classification}</small>}</span>
            </a>
          </article>
        ))}
        {!loading && !error && pagination && pagination.total_pages > 1 && (
          <nav className="search-pagination" aria-label="Pagination">
            <button disabled={pagination.current_page <= 1} onClick={() => movePage(pagination.current_page - 1)}>{t.previous}</button>
            <span>{pagination.current_page} / {pagination.total_pages}</span>
            <button disabled={pagination.current_page >= pagination.total_pages} onClick={() => movePage(pagination.current_page + 1)}>{t.next}</button>
          </nav>
        )}
      </section>
    </main>
  );
}
